import type { ComponentType } from "react";
import { AccentButton, Button } from "@wa/ui";
import type { AccentGlowVariant } from "@wa/ui";
import { Coins, CornerDownLeft, Eye, History, PencilLine, Plug, Send, ShieldCheck, Sparkles, UserRound } from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import type { ConsentRequest } from "../../lib/aiConnections";

type Busy = "allow" | "deny" | "switch" | null;
type Line = readonly [key: string, Icon: ComponentType<{ className?: string }>];

const CAN: readonly Line[] = [
  ["connect.canSee", Eye],
  ["connect.canEdit", PencilLine],
  ["connect.canPublish", Send],
];

const GOOD_TO_KNOW: readonly Line[] = [
  ["connect.role", ShieldCheck],
  ["connect.restore", History],
  ["connect.credit", Coins],
  ["connect.plan", Sparkles],
];

/**
 * The question itself: who asks, as whom, what it may do, where the answer goes.
 *
 * The app's name is whatever the app typed when it registered, so the one line
 * that can be trusted is the host the browser goes back to. It sits in its own
 * tinted box, right above the buttons, so it is the last thing read before Allow.
 * The website is shown as plain text for the same reason: a link would lend it
 * the authority of this page.
 */
export function ConsentView({
  request,
  email,
  busy,
  leaving,
  sendFailed,
  glow,
  onAnswer,
  onSwitchAccount,
}: {
  request: ConsentRequest;
  email: string;
  busy: Busy;
  /** The answer went through and the browser is on its way to the app. */
  leaving: boolean;
  sendFailed: boolean;
  glow: AccentGlowVariant;
  onAnswer: (allow: boolean) => void;
  onSwitchAccount: () => void;
}) {
  const t = useT();
  const locked = busy !== null;

  return (
    <div className="flex flex-col gap-5">
      <header className="flex items-start gap-3">
        <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Plug className="size-5" aria-hidden />
        </div>
        <div className="min-w-0">
          <h1 className="break-words text-xl font-bold tracking-tight">{t("connect.title", { app: request.clientName })}</h1>
          {request.clientWebsite && (
            <p className="mt-1 break-all text-xs text-muted-foreground">{t("connect.website", { site: request.clientWebsite })}</p>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md bg-muted/50 px-3 py-2">
        <span className="flex min-w-0 items-center gap-2 text-sm">
          <UserRound className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{t("connect.signedInAs", { email })}</span>
        </span>
        <Button variant="link" size="sm" className="h-auto px-0 py-1" loading={busy === "switch"} disabled={locked} onClick={onSwitchAccount}>
          {t("connect.switchAccount")}
        </Button>
      </div>

      <LineList title={t("connect.canTitle")} lines={CAN} tone="primary" />
      <LineList title={t("connect.goodToKnow")} lines={GOOD_TO_KNOW} tone="muted" />

      <div className="flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary/5 p-3">
        <CornerDownLeft className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm">{t("connect.returnLead")}</p>
          <p className="break-all text-base font-semibold">{request.redirectHost}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("connect.nameHint")}</p>
        </div>
      </div>

      {sendFailed && <p className="text-sm text-destructive">{t("connect.sendFailed")}</p>}
      {leaving && (
        <p role="status" className="text-sm text-muted-foreground">
          {t("connect.redirecting")}
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" size="lg" className="w-full" loading={busy === "deny"} disabled={locked} onClick={() => onAnswer(false)}>
          {t("connect.cancel")}
        </Button>
        <AccentButton
          glow={glow}
          size="lg"
          className="w-full"
          wrapperClassName="w-full"
          loading={busy === "allow"}
          disabled={locked}
          onClick={() => onAnswer(true)}
        >
          {t("connect.allow")}
        </AccentButton>
      </div>

      <p className="text-center text-xs text-muted-foreground">{t("connect.later")}</p>
    </div>
  );
}

function LineList({ title, lines, tone }: { title: string; lines: readonly Line[]; tone: "primary" | "muted" }) {
  const t = useT();
  return (
    <section>
      <h2 className="text-sm font-semibold">{title}</h2>
      <ul className="mt-2 space-y-2">
        {lines.map(([key, Icon]) => (
          <li key={key} className={tone === "primary" ? "flex items-start gap-2.5 text-sm" : "flex items-start gap-2.5 text-sm text-muted-foreground"}>
            <Icon className={tone === "primary" ? "mt-0.5 size-4 shrink-0 text-primary" : "mt-0.5 size-4 shrink-0"} />
            <span>{t(key)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
